import { locateRow } from "../find";
import { blockIdsInDragItem } from "../selection/block-bulk-ops";
import { rowIdsInDragItem } from "../selection/row-bulk-ops";
import { isBlockDrag, isSectionDrag } from "./rules";
import type { DocumentSection } from "../types";
import type { DragData, DropData } from "./types";

const SLOT_SNAP_PAD_X = 12;
const SLOT_SNAP_PAD_Y = 4;
const ROW_EDGE_SNAP = 14;
const BLOCK_INROW_INSET = 10;

export type DropSlotRect = {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
};

export type DropSlotCandidate = {
  id: string;
  data: DropData;
  rect: DropSlotRect;
};

type BlockHoverCandidate = {
  rowId: string;
  columnId: string;
  blockId: string;
  index: number;
  rect: DropSlotRect;
};

type RowHoverCandidate = {
  rowId: string;
  rect: DropSlotRect;
};

type GroupHoverCandidate = {
  groupId: string;
  sectionIndex: number;
  rect: DropSlotRect;
};

type ColumnHoverCandidate = {
  rowId: string;
  columnId: string;
  rect: DropSlotRect;
};

type DropCollisionContext = {
  sections?: DocumentSection[];
  blockHoverTargets?: BlockHoverCandidate[];
  columnHoverTargets?: ColumnHoverCandidate[];
  rowHoverTargets?: RowHoverCandidate[];
  groupHoverTargets?: GroupHoverCandidate[];
};

type ScoredSlot = {
  slot: DropSlotCandidate;
  distance: number;
};

export function isPointerNearSlotRect(
  pointer: { x: number; y: number },
  rect: Pick<DropSlotRect, 'left' | 'right' | 'top' | 'bottom'>,
): boolean {
  return (
    pointer.x >= rect.left - SLOT_SNAP_PAD_X &&
    pointer.x <= rect.right + SLOT_SNAP_PAD_X &&
    pointer.y >= rect.top - SLOT_SNAP_PAD_Y &&
    pointer.y <= rect.bottom + SLOT_SNAP_PAD_Y
  );
}

function isPointerInsideRect(
  pointer: { x: number; y: number },
  rect: Pick<DropSlotRect, 'left' | 'right' | 'top' | 'bottom'>,
): boolean {
  return (
    pointer.x >= rect.left &&
    pointer.x <= rect.right &&
    pointer.y >= rect.top &&
    pointer.y <= rect.bottom
  );
}

function boundaryY(rect: DropSlotRect): number {
  return rect.top + rect.height / 2;
}

function edgeDistanceY(pointer: { y: number }, rect: DropSlotRect): number {
  return Math.abs(pointer.y - boundaryY(rect));
}

function isRowBoundarySlot(data: DropData): boolean {
  return data.kind === 'section-slot' || data.kind === 'group-row-slot';
}

function isBlockInsertSlot(
  data: DropData,
): data is Extract<DropData, { kind: 'block-slot' }> {
  return data.kind === 'block-slot' && data.zone !== 'fill';
}

function pickClosestNearSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
): DropSlotCandidate | undefined {
  let best: DropSlotCandidate | undefined;
  let bestDistance = Infinity;

  for (const slot of slots) {
    if (!isPointerNearSlotRect(pointer, slot.rect)) {
      continue;
    }

    const distance = edgeDistanceY(pointer, slot.rect);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = slot;
    }
  }

  return best;
}

function scoreRowBoundarySlot(
  pointer: { x: number; y: number },
  slot: DropSlotCandidate,
): ScoredSlot | undefined {
  if (
    !isRowBoundarySlot(slot.data) ||
    !isPointerNearSlotRect(pointer, slot.rect)
  ) {
    return undefined;
  }

  const distance = edgeDistanceY(pointer, slot.rect);
  if (distance > ROW_EDGE_SNAP) {
    return undefined;
  }

  return { slot, distance };
}

function pickRowBoundarySlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
): DropSlotCandidate | undefined {
  let best: ScoredSlot | undefined;

  for (const slot of slots) {
    const scored = scoreRowBoundarySlot(pointer, slot);
    if (!scored) {
      continue;
    }
    if (!best || scored.distance < best.distance) {
      best = scored;
    }
  }

  return best?.slot;
}

function scoreBlockInsertSlot(
  pointer: { x: number; y: number },
  slot: DropSlotCandidate,
): ScoredSlot | undefined {
  if (
    !isBlockInsertSlot(slot.data) ||
    !isPointerNearSlotRect(pointer, slot.rect)
  ) {
    return undefined;
  }

  const line = boundaryY(slot.rect);
  const edge = slot.data.edge ?? 'between';

  if (edge === 'start') {
    if (pointer.y < line + BLOCK_INROW_INSET) {
      return undefined;
    }
    return { slot, distance: pointer.y - line - BLOCK_INROW_INSET };
  }

  if (edge === 'end') {
    if (pointer.y > line - BLOCK_INROW_INSET) {
      return undefined;
    }
    return { slot, distance: line - BLOCK_INROW_INSET - pointer.y };
  }

  return { slot, distance: edgeDistanceY(pointer, slot.rect) };
}

function isInsertAbovePointer(
  pointer: { y: number },
  rect: DropSlotRect,
): boolean {
  return pointer.y < boundaryY(rect);
}

function pickInnermostByRect<T extends { rect: DropSlotRect }>(
  pointer: { x: number; y: number },
  targets: T[],
): T | undefined {
  let best: T | undefined;
  let bestArea = Infinity;

  for (const target of targets) {
    if (!isPointerInsideRect(pointer, target.rect)) {
      continue;
    }

    const area = target.rect.width * target.rect.height;
    if (area < bestArea) {
      bestArea = area;
      best = target;
    }
  }

  return best;
}

function findEnabledSectionSlot(
  slots: DropSlotCandidate[],
  index: number,
): DropSlotCandidate | undefined {
  return slots.find(
    slot => slot.data.kind === 'section-slot' && slot.data.index === index,
  );
}

function findEnabledGroupRowSlot(
  slots: DropSlotCandidate[],
  groupId: string,
  index: number,
): DropSlotCandidate | undefined {
  return slots.find(
    slot =>
      slot.data.kind === 'group-row-slot' &&
      slot.data.groupId === groupId &&
      slot.data.index === index,
  );
}

function findEnabledBlockInsertSlot(
  slots: DropSlotCandidate[],
  rowId: string,
  columnId: string,
  index: number,
): DropSlotCandidate | undefined {
  return slots.find(
    slot =>
      slot.data.kind === 'block-slot' &&
      slot.data.zone !== 'fill' &&
      slot.data.rowId === rowId &&
      slot.data.columnId === columnId &&
      slot.data.index === index,
  );
}

function resolveRowHoverSlot(
  pointer: { y: number },
  row: RowHoverCandidate,
  slots: DropSlotCandidate[],
  sections: DocumentSection[],
): DropSlotCandidate | undefined {
  const located = locateRow(sections, row.rowId);
  if (!located) {
    return undefined;
  }

  const insertAbove = isInsertAbovePointer(pointer, row.rect);

  if (located.location.parent === 'document') {
    const slotIndex = insertAbove
      ? located.location.sectionIndex
      : located.location.sectionIndex + 1;
    return findEnabledSectionSlot(slots, slotIndex);
  }

  const slotIndex = insertAbove
    ? located.location.rowIndex
    : located.location.rowIndex + 1;
  return findEnabledGroupRowSlot(
    slots,
    located.location.groupId,
    slotIndex,
  );
}

function resolveGroupHoverSlot(
  pointer: { y: number },
  group: GroupHoverCandidate,
  slots: DropSlotCandidate[],
): DropSlotCandidate | undefined {
  const insertAbove = isInsertAbovePointer(pointer, group.rect);
  const slotIndex = insertAbove
    ? group.sectionIndex
    : group.sectionIndex + 1;
  return findEnabledSectionSlot(slots, slotIndex);
}

function pickBestContainerHoverInsertSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
  drag: DragData,
  context: DropCollisionContext,
  suppressSelfRowDrag: boolean,
): DropSlotCandidate | undefined {
  const sections = context.sections;
  if (!sections) {
    return undefined;
  }

  const row = pickInnermostByRect(pointer, context.rowHoverTargets ?? []);
  const group = pickInnermostByRect(pointer, context.groupHoverTargets ?? []);
  const useRow =
    row &&
    (!group ||
      row.rect.width * row.rect.height <= group.rect.width * group.rect.height);

  if (useRow && row) {
    if (
      (suppressSelfRowDrag &&
        drag.kind === 'item-row' &&
        rowIdsInDragItem(drag).includes(row.rowId)) ||
      (drag.kind === 'item-block' && drag.rowId === row.rowId)
    ) {
      return undefined;
    }
    return resolveRowHoverSlot(pointer, row, slots, sections);
  }

  if (group) {
    if (drag.kind === 'item-group' && drag.groupId === group.groupId) {
      return undefined;
    }
    return resolveGroupHoverSlot(pointer, group, slots);
  }

  return undefined;
}

function pickBestBlockHoverInsertSlot(
  pointer: { x: number; y: number },
  blocks: BlockHoverCandidate[],
  slots: DropSlotCandidate[],
  drag: DragData,
): DropSlotCandidate | undefined {
  const block = pickInnermostByRect(pointer, blocks);
  if (!block) {
    return undefined;
  }

  if (drag.kind === 'item-block' && blockIdsInDragItem(drag).includes(block.blockId)) {
    return undefined;
  }

  const slotIndex = isInsertAbovePointer(pointer, block.rect)
    ? block.index
    : block.index + 1;

  return findEnabledBlockInsertSlot(
    slots,
    block.rowId,
    block.columnId,
    slotIndex,
  );
}

function pickBestColumnHoverInsertSlot(
  pointer: { x: number; y: number },
  columns: ColumnHoverCandidate[],
  blocks: BlockHoverCandidate[],
  slots: DropSlotCandidate[],
  drag: DragData,
): DropSlotCandidate | undefined {
  const column = pickInnermostByRect(pointer, columns);
  if (!column) {
    return undefined;
  }

  const columnBlocks = blocks
    .filter(
      block =>
        block.rowId === column.rowId && block.columnId === column.columnId,
    )
    .sort((left, right) => left.index - right.index);

  if (columnBlocks.length === 0) {
    return undefined;
  }

  let insertIndex = 0;

  for (const block of columnBlocks) {
    if (drag.kind === 'item-block' && blockIdsInDragItem(drag).includes(block.blockId)) {
      continue;
    }

    if (isInsertAbovePointer(pointer, block.rect)) {
      return findEnabledBlockInsertSlot(
        slots,
        column.rowId,
        column.columnId,
        block.index,
      );
    }

    insertIndex = block.index + 1;
  }

  return findEnabledBlockInsertSlot(
    slots,
    column.rowId,
    column.columnId,
    insertIndex,
  );
}

function pickBestBlockDragInsertSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
  drag: DragData,
  context: DropCollisionContext = {},
): DropSlotCandidate | undefined {
  const blockHoverHit = pickBestBlockHoverInsertSlot(
    pointer,
    context.blockHoverTargets ?? [],
    slots,
    drag,
  );
  if (blockHoverHit) {
    return blockHoverHit;
  }

  const columnHoverHit = pickBestColumnHoverInsertSlot(
    pointer,
    context.columnHoverTargets ?? [],
    context.blockHoverTargets ?? [],
    slots,
    drag,
  );
  if (columnHoverHit) {
    return columnHoverHit;
  }

  const containerHit = pickBestContainerHoverInsertSlot(
    pointer,
    slots,
    drag,
    context,
    false,
  );

  let best: ScoredSlot | undefined;

  for (const slot of slots) {
    const scored = isRowBoundarySlot(slot.data)
      ? scoreRowBoundarySlot(pointer, slot)
      : scoreBlockInsertSlot(pointer, slot);
    if (!scored) {
      continue;
    }
    if (!best || scored.distance < best.distance) {
      best = scored;
    }
  }

  if (best?.slot) {
    return best.slot;
  }

  if (containerHit) {
    return containerHit;
  }

  return undefined;
}

function pickBestSectionDragInsertSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
  drag: DragData,
  context: DropCollisionContext = {},
): DropSlotCandidate | undefined {
  const containerHit = pickBestContainerHoverInsertSlot(
    pointer,
    slots,
    drag,
    context,
    true,
  );
  if (containerHit) {
    return containerHit;
  }

  const rowBoundaryHit = pickRowBoundarySlot(pointer, slots);
  if (rowBoundaryHit) {
    return rowBoundaryHit;
  }

  return pickClosestNearSlot(pointer, slots);
}

function pickContainingFillSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
): DropSlotCandidate | undefined {
  return pickInnermostByRect(pointer, slots);
}

function pickBestInsertSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
  drag: DragData | undefined,
  context: DropCollisionContext = {},
): DropSlotCandidate | undefined {
  if (drag && isBlockDrag(drag)) {
    return pickBestBlockDragInsertSlot(pointer, slots, drag, context);
  }

  if (drag && isSectionDrag(drag)) {
    return pickBestSectionDragInsertSlot(pointer, slots, drag, context);
  }

  return pickClosestNearSlot(pointer, slots);
}

function isPointerInSectionSlotHorizontalBand(
  pointer: { x: number },
  slots: DropSlotCandidate[],
): boolean {
  const sectionSlots = slots.filter(slot => slot.data.kind === 'section-slot');
  if (sectionSlots.length === 0) {
    return false;
  }

  const left = Math.min(...sectionSlots.map(slot => slot.rect.left));
  const right = Math.max(...sectionSlots.map(slot => slot.rect.right));
  return (
    pointer.x >= left - SLOT_SNAP_PAD_X && pointer.x <= right + SLOT_SNAP_PAD_X
  );
}

function measureContentVerticalBounds(
  context: DropCollisionContext,
): { top: number; bottom: number } | undefined {
  const rects = [
    ...(context.rowHoverTargets ?? []).map(target => target.rect),
    ...(context.groupHoverTargets ?? []).map(target => target.rect),
  ];
  if (rects.length === 0) {
    return undefined;
  }

  return {
    top: Math.min(...rects.map(rect => rect.top)),
    bottom: Math.max(...rects.map(rect => rect.bottom)),
  };
}

function pickCanvasEdgeSectionSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
  context: DropCollisionContext,
): DropSlotCandidate | undefined {
  const sections = context.sections;
  if (!sections || sections.length === 0) {
    return undefined;
  }
  if (!isPointerInSectionSlotHorizontalBand(pointer, slots)) {
    return undefined;
  }

  const contentBounds = measureContentVerticalBounds(context);
  if (!contentBounds) {
    return undefined;
  }

  if (pointer.y < contentBounds.top) {
    return findEnabledSectionSlot(slots, 0);
  }

  if (pointer.y > contentBounds.bottom) {
    return findEnabledSectionSlot(slots, sections.length);
  }

  return undefined;
}

export function pickBestDropSlot(
  pointer: { x: number; y: number },
  slots: DropSlotCandidate[],
  drag?: DragData,
  context: DropCollisionContext = {},
): DropSlotCandidate | undefined {
  const insertSlots = slots.filter(
    slot => slot.data.kind !== 'block-slot' || slot.data.zone !== 'fill',
  );
  const fillSlots = slots.filter(
    slot => slot.data.kind === 'block-slot' && slot.data.zone === 'fill',
  );

  if (drag && isBlockDrag(drag)) {
    const fillHit = pickContainingFillSlot(pointer, fillSlots);
    if (fillHit) {
      return fillHit;
    }
  }

  const insertHit = pickBestInsertSlot(pointer, insertSlots, drag, context);
  if (insertHit) {
    return insertHit;
  }

  const edgeHit = pickCanvasEdgeSectionSlot(pointer, insertSlots, context);
  if (edgeHit) {
    return edgeHit;
  }

  return pickContainingFillSlot(pointer, fillSlots);
}
